import { createClient } from '@supabase/supabase-js';

// Variables de configuración directamente desde .env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Verificar que tenemos los valores necesarios
if (!supabaseUrl) {
  console.error('CRÍTICO: La variable de entorno VITE_SUPABASE_URL debe estar definida en .env');
  throw new Error('VITE_SUPABASE_URL no está definida');
}
if (!supabaseAnonKey) {
  console.error('CRÍTICO: La variable de entorno VITE_SUPABASE_ANON_KEY debe estar definida en .env');
  throw new Error('VITE_SUPABASE_ANON_KEY no está definida');
}

console.log(`Inicializando cliente de Supabase con URL: ${supabaseUrl}`);
console.log(`API key utilizada (desde .env): ${supabaseAnonKey.substring(0, 10)}...`);

// Flags para detección de problemas (pueden eliminarse si no son necesarios)
let isRetrying = false;
let authErrorCount = 0;
const MAX_AUTH_ERRORS = 3;

// Función para crear una instancia de cliente configurada adecuadamente
function createSupabaseClient(url: string, key: string) {
  try {
    // Crear cliente con configuración explícita, OMITIENDO realtime aquí
    return createClient(url, key, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        storageKey: 'towatch-auth-storage',
        detectSessionInUrl: true,
        flowType: 'pkce'
      },
      // NO configurar realtime globalmente, se hará al crear el canal
      // realtime: {
      //   params: {
      //     apikey: key,
      //     eventsPerSecond: 10
      //   },
      //   timeout: 30000
      // },
      global: {
        headers: {
          'apikey': key, // Clave para REST/global (necesaria)
          // 'Authorization': `Bearer ${key}`, // Comentado: La librería debe gestionar el JWT del usuario
          'X-Client-Info': 'ToWatch Web App'
        }
      }
    });
  } catch (error) {
    console.error('Error al crear cliente Supabase:', error);
    throw error;
  }
}

// Crear cliente inicial
export let supabase = createSupabaseClient(supabaseUrl, supabaseAnonKey);

// Variable global para rastrear el estado de la conexión
let supabaseConnectionState = {
  lastReinitialized: 0,
  reinitCount: 0,
  errors: [],
  isReconnecting: false
};

// Función para obtener el estado de la conexión (para diagnóstico)
export function getSupabaseConnectionState() {
  return { ...supabaseConnectionState };
}

// Función para reinicializar completamente el cliente de Supabase
export function reinitializeSupabaseClient() {
  console.log('Reinicializando cliente de Supabase...');

  // Actualizar estado
  supabaseConnectionState.isReconnecting = true;
  supabaseConnectionState.lastReinitialized = Date.now();
  supabaseConnectionState.reinitCount++;

  try {
    // Crear un nuevo cliente
    const newClient = createSupabaseClient(supabaseUrl, supabaseAnonKey);

    // Intentar cerrar el cliente anterior si es posible
    try {
      if (typeof supabase.removeAllChannels === 'function') {
        supabase.removeAllChannels();
      }
      if (typeof supabase.realtime?.disconnect === 'function') {
        supabase.realtime.disconnect();
      }
    } catch (closeError) {
      console.warn('Error al cerrar cliente anterior:', closeError);
      supabaseConnectionState.errors.push({ time: Date.now(), error: 'Error al cerrar cliente anterior' });
    }

    // Reemplazar el cliente existente
    supabase = newClient;

    console.log('Cliente de Supabase reinicializado correctamente');
    supabaseConnectionState.isReconnecting = false;
    return true;
  } catch (error) {
    console.error('Error al reinicializar cliente de Supabase:', error);
    supabaseConnectionState.errors.push({ time: Date.now(), error: 'Error al reinicializar cliente' });
    supabaseConnectionState.isReconnecting = false;
    return false;
  }
}

// Verificar estructura básica del cliente
if (!supabase || !(supabase.auth) || !(supabase.from) || !(supabase.channel)) {
  console.error('CRÍTICO: El cliente de Supabase no se ha inicializado correctamente.');
  throw new Error('Fallo crítico al inicializar el cliente Supabase');
}

// Función para verificar y refrescar sesión si es necesario
export async function initializeAndCheckSession() {
  try {
    if (!supabase || !(supabase.auth)) {
      console.error('El cliente de Supabase (auth) no está inicializado correctamente');
      return false;
    }
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error al verificar la sesión:', error.message);
      return false;
    }
    if (data.session) {
      const expiresAt = data.session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      if (expiresAt && (expiresAt - now) < 600) {
        console.log('Sesión próxima a expirar, refrescando...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('Error al refrescar la sesión:', refreshError.message);
          return false;
        }
        console.log('Sesión refrescada correctamente');
      } else if (expiresAt) {
        console.log(`Sesión válida por ${Math.floor((expiresAt - now) / 60)} minutos más`);
      } else {
        console.log('No se pudo determinar caducidad de la sesión');
      }
    } else {
      console.log('No hay sesión activa, continuando como usuario anónimo');
    }
    return true;
  } catch (error: any) {
    console.error('Error al inicializar o verificar la sesión:', error?.message || error);
    return false;
  }
}

// Crear canales de forma segura
export function createSafeChannel(channelName: string, channelConfig?: any) {
  try {
    if (!supabase || !supabase.channel) {
      throw new Error('Cliente Supabase (channel) no inicializado');
    }
    // Pasar la configuración de realtime explícitamente si es necesario
    const config = channelConfig || { config: { realtime: { params: { apikey: supabaseAnonKey } } } };
    return supabase.channel(channelName, config);
  } catch (error) {
    console.error(`Error al crear canal ${channelName}:`, error);
    // Devolver objeto simulado
    return {
      on: () => { console.log(`[MOCK] Canal ${channelName}: registro on`); return { subscribe: () => { console.log(`[MOCK] Canal ${channelName}: on().subscribe()`); return { unsubscribe: () => {} }; } }; },
      subscribe: () => { console.log(`[MOCK] Canal ${channelName}: subscribe()`); return { unsubscribe: () => {} }; }
    };
  }
}

// Cerrar sesión
export const signOut = async () => {
  try {
    if (!supabase || !supabase.auth) {
      throw new Error('Cliente Supabase (auth) no inicializado');
    }
    await supabase.auth.signOut();
    localStorage.removeItem('towatch-user-data'); // Limpiar datos de usuario si los hubiera
    return true;
  } catch (error) {
    console.error('Error al cerrar sesión:', error);
    localStorage.removeItem('towatch-auth-storage');
    localStorage.removeItem('towatch-user-data');
    return false;
  }
};

// Función para manejar la reconexión cuando la página vuelve a tener foco
export async function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    console.log('Página visible de nuevo. Iniciando proceso de reconexión...');

    // Evitar reconexiones simultáneas
    if (supabaseConnectionState.isReconnecting) {
      console.log('Ya hay una reconexión en curso, esperando...');

      // Esperar a que termine la reconexión actual (máximo 5 segundos)
      const startWait = Date.now();
      while (supabaseConnectionState.isReconnecting && Date.now() - startWait < 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Si sigue reconectando después de esperar, forzar una nueva reconexión
      if (supabaseConnectionState.isReconnecting) {
        console.log('La reconexión anterior está tardando demasiado, forzando nueva reconexión');
        supabaseConnectionState.isReconnecting = false;
      } else {
        console.log('La reconexión anterior ha terminado, continuando');
        return true; // La reconexión anterior terminó, no es necesario hacer otra
      }
    }

    // Solución radical: Recrear completamente el cliente
    try {
      // Paso 1: Reinicializar el cliente de Supabase
      console.log('Reinicializando cliente de Supabase (solución radical)...');
      const clientReinitialized = reinitializeSupabaseClient();
      if (!clientReinitialized) {
        console.error('Error al reinicializar el cliente de Supabase');
        // Intentar continuar de todos modos
      }

      // Paso 2: Verificar y refrescar la sesión con reintento
      console.log('Verificando y refrescando sesión...');
      let sessionChecked = false;
      let retryCount = 0;

      while (!sessionChecked && retryCount < 3) {
        try {
          sessionChecked = await initializeAndCheckSession();
          if (!sessionChecked) {
            console.warn(`Intento ${retryCount + 1}/3: Error al verificar la sesión, reintentando...`);
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // Esperar más tiempo en cada reintento
          }
        } catch (error) {
          console.error(`Intento ${retryCount + 1}/3: Error al verificar la sesión:`, error);
          retryCount++;
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      if (!sessionChecked) {
        console.error('No se pudo verificar la sesión después de varios intentos');
        // Intentar continuar de todos modos
      }

      // Paso 3: Forzar una recarga de la sesión desde localStorage si está disponible
      try {
        const localSession = localStorage.getItem('supabase.auth.token');
        if (localSession) {
          console.log('Intentando restaurar sesión desde localStorage...');
          const parsedSession = JSON.parse(localSession);
          if (parsedSession && parsedSession.currentSession) {
            console.log('Sesión encontrada en localStorage, intentando restaurar...');
            await supabase.auth.setSession({
              access_token: parsedSession.currentSession.access_token,
              refresh_token: parsedSession.currentSession.refresh_token
            });
            console.log('Sesión restaurada desde localStorage');
          }
        }
      } catch (error) {
        console.error('Error al restaurar sesión desde localStorage:', error);
        // Continuar de todos modos
      }

      console.log('Reconexión completada');
      return true;
    } catch (error) {
      console.error('Error crítico durante la reconexión:', error);
      supabaseConnectionState.errors.push({ time: Date.now(), error: 'Error crítico durante la reconexión' });
      return false;
    } finally {
      supabaseConnectionState.isReconnecting = false;
    }
  }
  return false;
}

// Función para diagnosticar y solucionar problemas de conexión
export async function diagnoseAndFixConnection() {
  console.log('Ejecutando diagnóstico de conexión...');

  // Obtener estado actual
  const state = getSupabaseConnectionState();
  console.log('Estado actual de la conexión:', state);

  // Verificar si el cliente de Supabase está en buen estado
  let clientStatus = 'unknown';
  try {
    // Intentar una operación simple para verificar si el cliente funciona
    const { error } = await supabase.from('rooms').select('id').limit(1);
    clientStatus = error ? 'error' : 'ok';
    console.log(`Prueba de conexión: ${clientStatus}`, error || 'Sin errores');
  } catch (error) {
    clientStatus = 'error';
    console.error('Error al probar la conexión:', error);
  }

  // Si el cliente no está en buen estado, reinicializarlo
  if (clientStatus === 'error') {
    console.log('Cliente en mal estado, reinicializando...');
    const reinitialized = reinitializeSupabaseClient();
    console.log(`Reinicialización: ${reinitialized ? 'exitosa' : 'fallida'}`);
  }

  // Verificar la sesión
  let sessionStatus = 'unknown';
  try {
    const { data, error } = await supabase.auth.getSession();
    sessionStatus = data.session ? 'active' : 'inactive';
    console.log(`Estado de la sesión: ${sessionStatus}`, error || 'Sin errores');

    // Si no hay sesión activa, intentar restaurarla desde localStorage
    if (!data.session) {
      try {
        const localSession = localStorage.getItem('supabase.auth.token');
        if (localSession) {
          console.log('Intentando restaurar sesión desde localStorage...');
          const parsedSession = JSON.parse(localSession);
          if (parsedSession && parsedSession.currentSession) {
            await supabase.auth.setSession({
              access_token: parsedSession.currentSession.access_token,
              refresh_token: parsedSession.currentSession.refresh_token
            });
            console.log('Sesión restaurada desde localStorage');
            sessionStatus = 'restored';
          }
        }
      } catch (restoreError) {
        console.error('Error al restaurar sesión desde localStorage:', restoreError);
      }
    }
  } catch (error) {
    sessionStatus = 'error';
    console.error('Error al verificar la sesión:', error);
  }

  return {
    clientStatus,
    sessionStatus,
    connectionState: state
  };
}

// Verificar autenticación
export const checkAuth = async () => {
  try {
    if (!supabase || !supabase.auth) {
      throw new Error('Cliente Supabase (auth) no inicializado');
    }
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error al verificar autenticación:', error);
      return false;
    }
    return data.session !== null;
  } catch (error) {
    console.error('Error inesperado al verificar autenticación:', error);
    return false;
  }
};